import { ButtonIcon, Callout, CollectionIcon, Tooltip } from 'canoe';
import React, { useRef, useState } from 'react';

import { getAllowedFields } from '@candu-ai/collections/utils';
import { CollectionFieldsEditor } from 'craft/components/SelectedToolbar/Collections/CollectionFieldSelector/CollectionFieldsEditor';
import {
  PopoverContainer,
  PopoverTitle,
} from 'craft/components/SelectedToolbar/Collections/components/StyledComponents';
import { useAvailableCollectionFields } from 'craft/components/SelectedToolbar/Collections/hooks/useAvailableCollectionFields';
import { NO_ACTION } from 'craft/constants';
import { useMultiNodes, useSelection } from 'craft/hooks';
import { useViewportDom } from 'craft/state/viewport/selectors';
import { CollectionTargetLocation } from 'craft/types';

import { CollectionFallbacksEditor } from './CollectionFallbacksEditor';

export const CollectionFieldSelector: React.FC = () => {
  const ref = useRef<HTMLButtonElement>(null);
  const { selection } = useSelection();
  const [isToolbarVisible, setToolbarVisible] = useState(false);
  const nodeIds = selection.all;
  const { type, collections } = useMultiNodes(nodeIds, (node) => ({
    collections: node?.data.custom.collections,
  }));
  const { viewportWrapperEl } = useViewportDom();

  const availableFields = useAvailableCollectionFields({
    nodeIds,
  });

  const collectionEmbedLocations = getAllowedFields(type, availableFields);

  // do not render the button if no available schema field was found for the component type
  if (Object.values(collectionEmbedLocations).filter(Boolean).length < 1) {
    return null;
  }

  const nodeHasCollections = collections?.length > 0;

  return (
    <>
      {isToolbarVisible && (
        <Callout
          usePortal={false}
          referenceElement={ref.current}
          onClose={() => setToolbarVisible(false)}
          closeKey="Escape"
          placement="bottom"
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
          ]}
        >
          {(calloutProps) => (
            <div
              data-craft-ignore-events
              data-focus-listener={NO_ACTION}
              ref={calloutProps.ref}
              style={calloutProps.style}
              {...calloutProps.attributes.popper}
              css={{
                display: 'flex',
                alignItems:
                  calloutProps.attributes?.popper?.['data-popper-placement'] === 'top'
                    ? 'flex-end'
                    : 'flex-start',
              }}
            >
              <PopoverContainer data-cy="popover-container">
                <PopoverTitle>Collection Item Settings</PopoverTitle>
                <CollectionFieldsEditor
                  nodeIds={nodeIds}
                  collections={collections}
                  type={type}
                  collectionEmbedLocations={collectionEmbedLocations}
                  isPopover
                />
                <CollectionFallbacksEditor
                  nodeIds={nodeIds}
                  sortOrder={Object.keys(collectionEmbedLocations) as CollectionTargetLocation[]}
                  isPopover
                />
              </PopoverContainer>
            </div>
          )}
        </Callout>
      )}
      <Tooltip placement="top" content="Collection Item Settings">
        <ButtonIcon
          ref={ref}
          size="small"
          active={nodeHasCollections}
          icon={CollectionIcon}
          radius="small"
          color="white"
          data-cy="field-selector-button"
          onClick={() => {
            window.requestAnimationFrame(() => setToolbarVisible(!isToolbarVisible));
          }}
        />
      </Tooltip>
    </>
  );
};
